// Excluir categoria (dono, 26/10/2026: "loja nova, preciso sair apagando em
// massa"). Vazia: confirma e apaga. Com produtos: lista tudo o que vai sumir —
// sem venda é apagado de vez, com venda vai para Arquivados — e só apaga
// depois de marcar que entendeu que não tem volta.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Archive, Loader2, Trash2 } from "lucide-react";

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { excluirCategoria, mensagemDeErro, previaCategoria } from "./api";

const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;

export default function ExcluirCategoriaDialog({ categoria, aoFechar, aoExcluir }) {
  const [previa, setPrevia] = useState(null);
  const [erro, setErro] = useState("");
  const [entendi, setEntendi] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const comProdutos = (categoria?.products_count || 0) > 0;

  useEffect(() => {
    setPrevia(null); setErro(""); setEntendi(false);
    if (!categoria || !comProdutos) return;
    let vivo = true;
    previaCategoria(categoria.id)
      .then((p) => vivo && setPrevia(p))
      .catch((e) => vivo && setErro(mensagemDeErro(e, "Não foi possível listar os produtos")));
    return () => { vivo = false; };
  }, [categoria, comProdutos]);

  const produtos = previa?.produtos || [];
  const apagar = produtos.filter((p) => p.acao === "apagar");
  const arquivar = produtos.filter((p) => p.acao === "arquivar");
  const arquivadosAntes = comProdutos ? previa?.arquivados || 0 : categoria?.archived_count || 0;
  const pronto = !comProdutos || (previa && entendi);

  const confirmar = async (e) => {
    e.preventDefault();
    if (!pronto || enviando) return;
    setEnviando(true);
    try {
      const r = await excluirCategoria(categoria.id, comProdutos);
      const partes = [];
      if (r.produtos_apagados) partes.push(plural(r.produtos_apagados, "produto apagado", "produtos apagados"));
      if (r.produtos_arquivados) partes.push(plural(r.produtos_arquivados, "arquivado", "arquivados"));
      toast.success(`Categoria "${categoria.name}" excluída${partes.length ? ` — ${partes.join(", ")}` : ""}`);
      aoExcluir(r);
      aoFechar();
    } catch (err) {
      toast.error(mensagemDeErro(err, "Erro ao excluir categoria"));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <AlertDialog open={!!categoria} onOpenChange={(v) => { if (!v) aoFechar(); }}>
      <AlertDialogContent className="max-w-lg" data-e2e="excluir-categoria">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-red-500/15 flex items-center justify-center shrink-0">
              <Trash2 className="w-5 h-5 text-red-500" />
            </div>
            {comProdutos ? `Apagar "${categoria?.name}" e os produtos dela?` : "Excluir categoria permanentemente?"}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 pt-2 text-sm">
              {!comProdutos && (
                <p>A categoria <strong className="text-foreground">{categoria?.name}</strong> será removida do sistema.</p>
              )}
              {comProdutos && !previa && !erro && (
                <p className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Conferindo as vendas de cada produto…</p>
              )}
              {erro && <p className="text-red-500">{erro}</p>}
              {previa && (
                <>
                  <p>
                    Tudo o que está nesta categoria vai sair do cardápio:
                    {apagar.length > 0 && <> <strong className="text-foreground">{plural(apagar.length, "produto será apagado", "produtos serão apagados")}</strong> de vez</>}
                    {apagar.length > 0 && arquivar.length > 0 && " e"}
                    {arquivar.length > 0 && <> <strong className="text-foreground">{plural(arquivar.length, "vai", "vão")}</strong> para Arquivados (têm histórico)</>}.
                  </p>
                  <ul className="max-h-64 overflow-y-auto rounded-lg border divide-y text-foreground" data-e2e="excluir-categoria-lista">
                    {produtos.map((p) => (
                      <li key={p.id} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
                        <span className="min-w-0 truncate">{p.name}</span>
                        {p.acao === "apagar" ? (
                          <span className="shrink-0 inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-red-500/10 text-red-600 dark:text-red-400 font-medium">
                            <Trash2 className="w-3 h-3" /> Apagar
                          </span>
                        ) : (
                          <span className="shrink-0 inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-amber-500/10 text-amber-600 dark:text-amber-400 font-medium" title={p.motivo}>
                            <Archive className="w-3 h-3" /> Arquivar · {p.motivo}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {arquivadosAntes > 0 && (
                <p className="text-xs">
                  {plural(arquivadosAntes, "produto já arquivado usa", "produtos já arquivados usam")} esta categoria e
                  {arquivadosAntes === 1 ? " ficará" : " ficarão"} sem categoria.
                </p>
              )}
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-foreground/80 flex gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-px" />
                <span><strong className="text-red-500">Não tem volta.</strong> O que for apagado não vai para Arquivados e não pode ser recuperado.</span>
              </div>
              {comProdutos && previa && (
                <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer select-none">
                  <input type="checkbox" checked={entendi} onChange={(e) => setEntendi(e.target.checked)}
                    className="w-4 h-4 accent-red-600" data-e2e="excluir-categoria-entendi" />
                  Entendi, pode apagar a categoria e os produtos
                </label>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={enviando}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={confirmar} disabled={!pronto || enviando} data-e2e="excluir-categoria-confirmar"
            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">
            {enviando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
            {comProdutos ? `Apagar categoria e ${plural(produtos.length || categoria?.products_count || 0, "produto", "produtos")}` : "Sim, excluir"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
